'use client';

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Checks if an error is related to PostHog or is a stack overflow that should be caught.
 * This function is designed to catch errors from minified PostHog code as well.
 */
const isPostHogRelatedError = (error: Error): boolean => {
  const message = error.message || '';
  const stack = error.stack || '';

  // Always catch "Maximum call stack size exceeded" errors - these are almost always
  // from PostHog's internal recursion issues with feature flags
  if (message.includes('Maximum call stack size exceeded')) {
    return true;
  }

  // Check for PostHog-specific patterns in stack trace
  const postHogPatterns = [
    'posthog',
    'PostHog',
    '_execute_array',
    'getFeatureFlag',
    'isFeatureEnabled',
    'onFeatureFlags',
    // Minified PostHog patterns (from the error stack trace)
    'np.<anonymous>',
    'np.push',
    'np._execute_array',
    // Common minified function patterns in PostHog
    /\bnp\b/,
    /\bth\b/,
  ];

  for (const pattern of postHogPatterns) {
    if (typeof pattern === 'string') {
      if (stack.includes(pattern)) return true;
    } else if (pattern.test(stack)) {
      return true;
    }
  }

  return false;
};

/**
 * Error boundary specifically for catching PostHog-related errors
 * (e.g., "Maximum call stack size exceeded" from infinite recursion in feature flags)
 *
 * This boundary catches the error and continues rendering children,
 * preventing the entire page from crashing due to PostHog issues.
 */
class PostHogErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a PostHog-related or stack overflow error
    // Note: We catch ALL stack overflow errors here because they typically
    // originate from PostHog's feature flag recursion, and letting them
    // propagate will crash the entire page regardless.
    if (isPostHogRelatedError(error)) {
      return { hasError: true, error };
    }

    // For non-PostHog errors, still set hasError but let componentDidCatch
    // decide whether to re-throw. This prevents the static method from throwing.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isPostHogError = isPostHogRelatedError(error);

    if (isPostHogError) {
      // Log PostHog errors for debugging but don't crash the app
      // eslint-disable-next-line no-console
      console.warn('[PostHogErrorBoundary] Caught PostHog error:', {
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 5).join('\n'),
        componentStack: errorInfo.componentStack?.split('\n').slice(0, 5).join('\n'),
      });
      // Reset error state so we can continue rendering
      this.setState({ hasError: false, error: null });
    } else {
      // For non-PostHog errors, log and re-throw to propagate to parent error boundaries
      // eslint-disable-next-line no-console
      console.error('[PostHogErrorBoundary] Non-PostHog error caught, re-throwing:', error);
      throw error;
    }
  }

  render(): ReactNode {
    // If we have a non-PostHog error that hasn't been re-thrown yet, render fallback
    if (this.state.hasError && this.state.error && !isPostHogRelatedError(this.state.error)) {
      return this.props.fallback || this.props.children;
    }
    // Always render children - PostHog errors shouldn't break the UI
    // The feature flags will just return default values
    return this.props.children;
  }
}

export default PostHogErrorBoundary;
